import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ArrowUp, Loader2, Search } from 'lucide-react';
import { suggestAskGuruQueries, splitQuerySuggestionHighlight } from '../../lib/askBhriguQuerySuggestions';
import { SmoothExpand } from '../shared/SmoothExpand';

/**
 * @param {{
 *   value: string;
 *   onChange: (v: string) => void;
 *   onSubmit: () => void;
 *   showEnglish: boolean;
 *   isDark: boolean;
 *   loading?: boolean;
 *   placeholder?: string;
 *   compact?: boolean;
 *   embedded?: boolean;
 *   transparentShell?: boolean;
 *   variant?: 'landing' | 'chat';
 *   onPickSuggestion?: (text: string) => void;
 *   onFocus?: () => void;
 *   canSubmit?: boolean;
 *   validationHint?: string;
 * }} props
 */
export function BhriguChatComposer({
  value,
  onChange,
  onSubmit,
  showEnglish,
  isDark,
  loading = false,
  placeholder,
  compact = false,
  embedded = false,
  transparentShell = false,
  variant = 'landing',
  onPickSuggestion,
  onFocus,
  canSubmit,
  validationHint = '',
}) {
  const isChat = variant === 'chat';
  const defaultPlaceholder = showEnglish
    ? isChat
      ? 'Type a message…'
      : 'Ask about rashis, dashas, nakshatras, yogas…'
    : isChat
      ? 'संदेश लिखें…'
      : 'राशि, दशा, नक्षत्र, योग… पूछें';

  const rootRef = useRef(null);
  const textareaRef = useRef(null);
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const suggestions = useMemo(
    () => (isChat ? [] : suggestAskGuruQueries(value, { showEnglish, limit: 8 })),
    [value, showEnglish, isChat],
  );

  const submitEnabled = canSubmit ?? Boolean(value.trim());

  const showPanel = !isChat && open && value.trim().length >= 2 && suggestions.length > 0 && !loading;

  useEffect(() => {
    setActiveIndex(-1);
  }, [value, suggestions.length]);

  useEffect(() => {
    if (isChat && !loading) {
      textareaRef.current?.focus();
    }
  }, [isChat, loading]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pickSuggestion = useCallback(
    (text) => {
      onChange(text);
      setOpen(false);
      setActiveIndex(-1);
      if (onPickSuggestion) {
        onPickSuggestion(text);
      } else {
        textareaRef.current?.focus();
      }
    },
    [onChange, onPickSuggestion],
  );

  const handleKeyDown = (e) => {
    if (showPanel && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
        return;
      }
      if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        pickSuggestion(suggestions[activeIndex].text);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        setActiveIndex(-1);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!loading && submitEnabled) {
        setOpen(false);
        onSubmit();
      }
    }
  };

  const muted = isDark ? 'text-gray-500' : 'text-gray-500';

  const fieldShell = transparentShell
    ? 'ask-bhrigu-transparent-field focus-within:ring-0'
    : isDark
      ? isChat
        ? 'border-white/12 bg-white/[0.06] focus-within:ring-2 focus-within:ring-violet-400/30'
        : 'border-white/15 bg-black/40 focus-within:ring-2 focus-within:ring-violet-400/40'
      : isChat
        ? 'border-gray-200 bg-white focus-within:ring-2 focus-within:ring-violet-300'
        : 'border-amber-200 bg-white focus-within:ring-2 focus-within:ring-violet-300';

  const fieldText = transparentShell
    ? isDark
      ? 'text-white placeholder:text-white/55'
      : 'text-stone-900 placeholder:text-stone-600'
    : isDark
      ? 'text-white placeholder:text-gray-500'
      : 'text-gray-900 placeholder:text-gray-400';

  const hintLine = validationHint ? (
    <p
      className={`ask-bhrigu-readable-muted mt-1.5 px-1 text-[11px] leading-snug ${transparentShell ? '' : muted} ${showEnglish ? '' : 'font-devanagari'}`}
      lang={showEnglish ? 'en' : 'hi'}
      role="status"
    >
      {validationHint}
    </p>
  ) : null;

  if (isChat) {
    return (
      <div ref={rootRef} className="relative">
        <div className={`flex items-end gap-2 rounded-3xl border px-3 py-1.5 ${fieldShell}`}>
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => {
              onFocus?.();
            }}
            onKeyDown={handleKeyDown}
            rows={1}
            maxLength={2000}
            placeholder={placeholder || defaultPlaceholder}
            className={`max-h-24 min-w-0 flex-1 resize-none border-0 bg-transparent py-2 text-sm leading-relaxed outline-none ring-0 focus:ring-0 ${fieldText} ${showEnglish ? '' : 'font-devanagari'}`}
            lang={showEnglish ? 'en' : 'hi'}
            aria-label={showEnglish ? 'Your message' : 'आपका संदेश'}
          />
          <button
            type="button"
            onClick={onSubmit}
            disabled={loading || !submitEnabled}
            className={`mb-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-all disabled:opacity-45 ${
              isDark
                ? 'bg-violet-500 text-white hover:bg-violet-400'
                : 'bg-violet-600 text-white hover:bg-violet-500'
            }`}
            aria-label={showEnglish ? 'Send' : 'भेजें'}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <ArrowUp className="h-4 w-4" aria-hidden />
            )}
          </button>
        </div>
        {hintLine}
      </div>
    );
  }

  return (
    <div ref={rootRef} className={`relative ${embedded ? 'z-[1]' : ''}`}>
      {embedded && showPanel ? (
        <SmoothExpand
          open={showPanel}
          className="absolute bottom-[calc(100%+4px)] left-0 right-0 z-[90]"
        >
          <div
            id={listId}
            role="listbox"
            aria-label={showEnglish ? 'Suggested questions' : 'सुझाए गए प्रश्न'}
            className={`max-h-32 overflow-y-auto rounded-xl border shadow-lg ${
              isDark
                ? 'border-white/12 bg-gray-950/98 shadow-black/40 backdrop-blur-md'
                : 'border-gray-200 bg-white shadow-black/10'
            }`}
          >
            <ul className="p-1">
              {suggestions.map((s, i) => {
                const { matched, completion } = splitQuerySuggestionHighlight(value, s.text);
                const active = i === activeIndex;
                return (
                  <li key={s.text} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={active}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setActiveIndex(i)}
                      onClick={() => pickSuggestion(s.text)}
                      className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs transition-colors ${
                        active
                          ? isDark
                            ? 'bg-violet-500/20 text-white'
                            : 'bg-violet-50 text-gray-900'
                          : isDark
                            ? 'text-gray-200 hover:bg-white/5'
                            : 'text-gray-800 hover:bg-gray-50'
                      } ${showEnglish ? '' : 'font-devanagari'}`}
                    >
                      <Search className={`h-3 w-3 shrink-0 ${muted}`} aria-hidden />
                      <span className="min-w-0 truncate">
                        <span className={isDark ? 'text-gray-100' : 'text-gray-900'}>{matched}</span>
                        {completion ? (
                          <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{completion}</span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </SmoothExpand>
      ) : null}

      <div className={`flex items-end gap-2 rounded-xl border p-1.5 ${fieldShell}`}>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
            onFocus?.();
          }}
          onKeyDown={handleKeyDown}
          rows={embedded ? 3 : compact ? 2 : 3}
          maxLength={2000}
          placeholder={placeholder || defaultPlaceholder}
          className={`min-w-0 flex-1 resize-none border-0 bg-transparent px-2 py-2 text-sm leading-relaxed outline-none ring-0 focus:ring-0 ${
            embedded ? 'min-h-[4.25rem] max-h-32' : ''
          } ${fieldText} ${showEnglish ? '' : 'font-devanagari'}`}
          lang={showEnglish ? 'en' : 'hi'}
          aria-label={showEnglish ? 'Your question' : 'आपका प्रश्न'}
          aria-autocomplete="list"
          aria-controls={showPanel ? listId : undefined}
        />

        <button
          type="button"
          onClick={() => {
            if (loading || !submitEnabled) return;
            setOpen(false);
            onSubmit();
          }}
          disabled={loading || !submitEnabled}
          className={`mb-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-all disabled:opacity-40 ${
            transparentShell
              ? isDark
                ? 'bg-white/15 text-white hover:bg-white/25'
                : 'bg-stone-900/85 text-white hover:bg-stone-900'
              : isDark
                ? 'bg-violet-500 text-white hover:bg-violet-400'
                : 'bg-violet-600 text-white hover:bg-violet-500'
          }`}
          aria-label={showEnglish ? 'Send' : 'भेजें'}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <ArrowUp className="h-4 w-4" aria-hidden />
          )}
        </button>
      </div>

      {!embedded ? (
        <SmoothExpand open={showPanel} className="absolute left-0 right-0 top-[calc(100%+4px)] z-[80]">
          <div
            id={listId}
            role="listbox"
            aria-label={showEnglish ? 'Suggested questions' : 'सुझाए गए प्रश्न'}
            className={`max-h-[min(50dvh,16rem)] overflow-y-auto rounded-xl border shadow-lg ${
              isDark
                ? 'border-white/12 bg-gray-950/98 shadow-black/40 backdrop-blur-md'
                : 'border-gray-200 bg-white shadow-black/10'
            }`}
          >
            <ul className="p-1">
              {suggestions.map((s, i) => {
                const { matched, completion } = splitQuerySuggestionHighlight(value, s.text);
                const active = i === activeIndex;
                return (
                  <li key={s.text} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={active}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setActiveIndex(i)}
                      onClick={() => pickSuggestion(s.text)}
                      className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                        active
                          ? isDark
                            ? 'bg-violet-500/20 text-white'
                            : 'bg-violet-50 text-gray-900'
                          : isDark
                            ? 'text-gray-200 hover:bg-white/5'
                            : 'text-gray-800 hover:bg-gray-50'
                      } ${showEnglish ? '' : 'font-devanagari'}`}
                    >
                      <Search className={`h-3.5 w-3.5 shrink-0 ${muted}`} aria-hidden />
                      <span className="min-w-0 truncate">
                        <span className={isDark ? 'text-gray-100' : 'text-gray-900'}>{matched}</span>
                        {completion ? (
                          <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{completion}</span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </SmoothExpand>
      ) : null}
      {hintLine}
    </div>
  );
}
